import {
  CombinedState,
  Dispatch,
  EnhancedStore,
  PreloadedState,
  Store,
  configureStore
} from '@reduxjs/toolkit'
import React, { ReactNode } from 'react'
import {
  Provider,
  ReactReduxContextValue,
  createDispatchHook,
  createSelectorHook,
  createStoreHook
} from 'react-redux'

export type ReducersForState<State> =
  | {
      [key: string]: (state: State, ...args: any[]) => State
    }
  | ((state: State, action: any) => State)

type ValuesOf<T> = T[keyof T]

type ActionForReducers<
  State,
  Reducers extends ReducersForState<State>
> = Reducers extends (state: State, action: infer Action) => State
  ? Action
  : ValuesOf<{
      [key in keyof Reducers]: {
        type: key
        args: Reducers[key] extends (state: State, ...args: infer Args) => State
          ? Args
          : never
      }
    }>

type ActionsForReducers<
  State,
  Reducers extends ReducersForState<State>
> = Reducers extends Function ? undefined : Reducers

type DispatchMethods<
  State,
  Reducers extends ReducersForState<State>
> = Reducers extends (state: State, action: any) => State
  ? {}
  : {
      [key in keyof Reducers]: Reducers[key] extends (
        state: State,
        ...args: infer Args
      ) => State
        ? (...args: Args) => { type: key; args: Args }
        : never
    }

type StoreType<State, Reducers extends ReducersForState<State>> = EnhancedStore<
  State,
  ActionForReducers<State, Reducers>
>

type DispatchType<State, Reducers extends ReducersForState<State>> = Dispatch<
  ActionForReducers<State, Reducers>
> &
  DispatchMethods<State, Reducers>

const identity = <State,>(state: State) => state

interface StoreProto<State, Reducers extends ReducersForState<State>> {
  context: React.Context<ReactReduxContextValue>
  actions: ActionsForReducers<State, Reducers>
  reducer: (
    state: State | undefined,
    action: ActionForReducers<State, Reducers>
  ) => State
  initialState: State
  useStore: () => Store<State, ActionForReducers<State, Reducers>>
  useDispatch: () => Dispatch<ActionForReducers<State, Reducers>>
  selector: <Selected extends unknown>(
    selector: (state: State) => Selected,
    equalityFn?: (previous: Selected, next: Selected) => boolean
  ) => Selected
}

class StoreBase<State, Reducers extends ReducersForState<State>> {
  store: StoreType<State, Reducers>
  dispatch: DispatchType<State, Reducers>

  constructor(initialState?: Partial<State>) {
    const proto = this.constructor as Function & StoreProto<State, Reducers>

    this.store = configureStore({
      reducer: proto.reducer,
      preloadedState: (initialState
        ? { ...proto.initialState, ...initialState }
        : proto.initialState) as PreloadedState<CombinedState<State>>,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: {
            ignoredActionPaths: ['args', 'value']
          }
        })
    })

    const actions: Partial<DispatchMethods<State, Reducers>> = {}
    if (proto.actions) {
      for (const type in proto.actions) {
        actions[type as any as keyof typeof actions] = ((...args: any[]) =>
          this.dispatch({ type, args } as any)) as any
      }
    }

    this.dispatch = Object.assign(
      this.store.dispatch,
      actions as DispatchMethods<State, Reducers>
    )
  }

  getState(): State {
    return this.store.getState()
  }

  subscribe<Selected extends unknown>(
    selector: (state: State) => Selected,
    callback: (state: Selected) => void
  ) {
    let prevState = selector(this.store.getState())
    return this.store.subscribe(() => {
      const state = selector(this.store.getState())
      if (state !== prevState) {
        prevState = state
        callback(state)
      }
    })
  }
}

export default function createStore<
  State,
  Reducers extends ReducersForState<State>
>(reducers: Reducers, initialState: State) {
  type Action = ActionForReducers<State, Reducers>
  type Actions = ActionsForReducers<State, Reducers>
  type Reducer = (state: State | undefined, action: Action) => State

  const context = React.createContext<ReactReduxContextValue<State, Action>>(
    undefined!
  )

  return class CustomStore extends StoreBase<State, Reducers> {
    static context = context
    static actions = (typeof reducers === 'function'
      ? undefined
      : reducers) as Actions
    static reducer = (typeof reducers === 'function'
      ? reducers
      : (state: State, action: Action) =>
          action.type in reducers
            ? reducers[action.type](state, ...action.args)
            : state) as Reducer
    static initialState = initialState
    static useStore = createStoreHook(context)
    static useDispatch = createDispatchHook(context) as () => DispatchType<
      State,
      Reducers
    >
    static selector = createSelectorHook(context)

    static Provider = ({
      store,
      children
    }: {
      store: CustomStore
      children: ReactNode
    }) => {
      return (
        <Provider
          context={context as unknown as React.Context<ReactReduxContextValue>}
          store={store.store}>
          {children}
        </Provider>
      )
    }

    static useSelector<Selected extends unknown>(
      func: (state: State) => Selected,
      depends?: React.DependencyList
    ): Selected {
      // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
      if (depends) func = React.useCallback(func, depends)
      return this.selector(func)
    }

    static useState(): State {
      return this.selector(identity)
    }
  }
}
